import { useMemo, useState } from 'react';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_TRABAJOS } from '@/lib/filtros-esquemas';
import {
  RANGOS,
  aRangoConsulta,
  esRangoValido,
  resolverRango,
  type TokenRango,
} from '@/lib/rango-fechas';

/** Los filtros que esta pantalla guarda en la URL. */
interface FiltrosTrabajos {
  campo?: boolean;
  estado?: JobStatus;
  tecnico?: string;
  rango?: TokenRango;
  desde?: string;
  hasta?: string;
  vencidos?: boolean;
  fueraDeRadio?: boolean;
  /** Solo garantías: la lista del encargado de garantías. */
  garantia?: boolean;
  pruebas?: boolean;
  buscar?: string;
  orden?: OrdenTrabajos;
}

/** Los tres órdenes que ofrece la pantalla. El default es la cita más próxima. */
type OrdenTrabajos = 'cita_proxima' | 'cita_lejana' | 'recientes';

const ordenOptions: { value: OrdenTrabajos; label: string }[] = [
  { value: 'cita_proxima', label: 'Cita más próxima' },
  { value: 'cita_lejana', label: 'Cita más lejana' },
  { value: 'recientes', label: 'Últimos registrados' },
];
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wrench, MapPin, Star, CalendarClock, MessageSquareQuote, Trash2, ClipboardList, Pencil, StickyNote, Plus, History, Ban, UserX, ClipboardEdit, ShieldCheck, ShieldAlert, PhoneCall, ArrowUpRight, Download, Link2, FlaskConical } from 'lucide-react';
import type { JobStatus } from '@conversia/shared-types';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import * as employeesApi from '@/api/employees.api';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { JobTimeline } from '@/components/tracking/JobTimeline';
import { EditarJobModal } from '@/components/jobs/EditarJobModal';
import { JobFormModal } from '@/components/jobs/JobFormModal';
import { JobHistoryModal } from '@/components/jobs/JobHistoryModal';
import { CancelarJobModal } from '@/components/jobs/CancelarJobModal';
import { EditarCierreModal } from '@/components/jobs/EditarCierreModal';
import { ValidarCierreModal } from '@/components/jobs/ValidarCierreModal';
import { NuevaGarantiaModal } from '@/components/jobs/NuevaGarantiaModal';
import { MenuAcciones, type AccionDeMenu } from '@/components/jobs/MenuAcciones';
import { VincularConversacionModal } from '@/components/jobs/VincularConversacionModal';
import { prioridadGarantiaUI, separarGarantiasVencidas } from '@/lib/garantia-prioridad';
import { fichaIncompletaUI } from '@/lib/garantia-ficha';
import {
  CerrarCasoRemotoModal,
  EscalarCasoModal,
} from '@/components/jobs/CasoRemotoModals';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { jobStatusBadge } from '@/lib/badges';
import { closureZoneLabel } from '@/lib/closure-zone';
import { esperaPendiente, groupJobs } from '@/lib/job-groups';
import { toInputValue } from '@/lib/despachos-fechas';
import { cn } from '@/lib/cn';
import { useToast } from '@/hooks/useToast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDistanceM } from '@conversia/shared-types';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { VistaRapidaTrabajo } from '@/components/jobs/VistaRapidaTrabajo';
import { Cargando } from '@/components/common/Cargando';

/** ¿El borrado exige la doble confirmación? (completado con opinión del cliente). */
function esBorradoProtegido(job: JobWithRelations): boolean {
  return job.estado === 'COMPLETADO' && Boolean(job.surveyResponse);
}

const estadoFilterOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'ASIGNADO', label: 'Asignado' },
  { value: 'EN_SITIO', label: 'En sitio' },
  // S-01: la cola de trabajos que el técnico cerró y esperan a supervisión.
  { value: 'PENDIENTE_VALIDACION', label: 'Por validar' },
  { value: 'COMPLETADO', label: 'Completado' },
  { value: 'NO_COMPLETADO', label: 'No completado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

// El cambio MANUAL de estado deja fuera CANCELADO y PENDIENTE_VALIDACION:
// ambos tienen su propia puerta (motivo obligatorio / cierre con cobro) y el
// backend los rechaza acá. Se listan explícitamente — un `slice` se rompe solo
// en cuanto se agrega un estado nuevo.
const estadoOptions = [
  { value: 'ASIGNADO', label: 'Asignado' },
  { value: 'EN_SITIO', label: 'En sitio' },
  { value: 'COMPLETADO', label: 'Completado' },
  { value: 'NO_COMPLETADO', label: 'No completado' },
];

/** Filtro de fecha de la cita: chips rápidos + rango a medida. */

// A1: los chips salen del catalogo unificado, no de una lista propia. "todo"
// se muestra primero porque es el estado por defecto de la pantalla.
const fechaChips: { value: TokenRango | 'personalizado'; label: string }[] = [
  { value: 'todo', label: 'Todas las fechas' },
  ...RANGOS.filter((r) => r.token !== 'todo').map((r) => ({ value: r.token, label: r.etiqueta })),
  // El rango a medida no es un token: es "sin token, con fechas explicitas".
  // Asi la URL queda ?desde=...&hasta=... y se lee sola.
  { value: 'personalizado', label: 'Rango' },
];

/** "2 jul, 3:30 p. m." desde el ISO de la cita, o null si no hay. */
function citaLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function Jobs() {
  const qc = useQueryClient();
  const toast = useToast();
  // A1: los filtros viven en la URL. Recargar los conserva, atras/adelante
  // funcionan y el enlace se puede compartir -- que es lo que hace posible que
  // el tablero (A5) y el copiloto (B4) abran esta pantalla ya filtrada.
  const { filtros: filtrosUrl, setFiltros } = useFiltrosUrl<FiltrosTrabajos>(ESQUEMA_TRABAJOS);

  const estado: JobStatus | '' = filtrosUrl.estado ?? '';
  const setEstado = (v: JobStatus | '') => setFiltros({ estado: v || undefined });

  const employeeId = filtrosUrl.tecnico ?? '';
  const setEmployeeId = (v: string) => setFiltros({ tecnico: v || undefined });

  // El chip de fecha activo. Sin token en la URL, "todas las fechas".
  const hayFechasPropias = Boolean(filtrosUrl.desde || filtrosUrl.hasta);
  const fecha: TokenRango | 'personalizado' = esRangoValido(filtrosUrl.rango)
    ? (filtrosUrl.rango as TokenRango)
    : hayFechasPropias
      ? 'personalizado'
      : 'todo';

  const setFecha = (v: TokenRango | 'personalizado') => {
    if (v === 'personalizado') {
      // Al entrar al modo a medida se siembran las dos fechas con hoy, para que
      // el usuario vea de que dia parte en vez de dos campos vacios.
      setFiltros({ rango: undefined, desde: hoyInput, hasta: hoyInput });
      return;
    }
    setFiltros({ rango: v === 'todo' ? undefined : v, desde: undefined, hasta: undefined });
  };
  const hoyInput = toInputValue(new Date());
  const desde = filtrosUrl.desde ?? hoyInput;
  const hasta = filtrosUrl.hasta ?? hoyInput;
  const setDesde = (v: string) => setFiltros({ desde: v || undefined });
  const setHasta = (v: string) => setFiltros({ hasta: v || undefined });

  // El rango se RESUELVE al leer, nunca se guarda calculado: un enlace con
  // `rango=manana` sigue significando manana el mes que viene.
  const rango = useMemo(
    () => aRangoConsulta(resolverRango({ rango: filtrosUrl.rango, desde: filtrosUrl.desde, hasta: filtrosUrl.hasta })),
    [filtrosUrl.rango, filtrosUrl.desde, filtrosUrl.hasta],
  );
  // S-10 · Filtro "Vencidos": el MISMO conjunto que marca vencido el Tracking
  // de campo (trabajo abierto con el plazo pasado). La definición vive en el
  // backend para que las dos pantallas no puedan contradecirse.
  const soloVencidos = filtrosUrl.vencidos === true;
  const setSoloVencidos = (v: boolean) => setFiltros({ vencidos: v || undefined });
  // S-05 · Los cierres que quedaron fuera del radio del destino.
  const soloFueraDeRadio = filtrosUrl.fueraDeRadio === true;
  const setSoloFueraDeRadio = (v: boolean) => setFiltros({ fueraDeRadio: v || undefined });

  // Solo garantías: lo pidió el cliente para el encargado de garantías, que no
  // trabaja la lista completa. Es el filtro que enciende el atajo del menú.
  const soloGarantias = filtrosUrl.garantia === true;
  const setSoloGarantias = (v: boolean) => setFiltros({ garantia: v || undefined });
  // Búsqueda y orden. Viven en la URL como el resto: así un enlace a "los
  // trabajos de Ana ordenados por cita" se puede compartir y muestra lo mismo.
  const buscar = filtrosUrl.buscar ?? '';
  const setBuscar = (v: string) => setFiltros({ buscar: v || undefined });
  // Se consulta con retraso: escribir "villa mella" dispararía diez consultas,
  // una por tecla. La URL sí se actualiza al instante (con `replace`, sin
  // ensuciar el historial) para que el enlace siempre refleje lo que se ve.
  const buscarDebounced = useDebouncedValue(buscar, 300);
  const orden: OrdenTrabajos = filtrosUrl.orden ?? 'cita_proxima';
  const setOrden = (v: OrdenTrabajos) =>
    setFiltros({ orden: v === 'cita_proxima' ? undefined : v });
  // Registros de PRUEBA: se excluyen salvo que se pidan explícitamente.
  const soloPruebas = filtrosUrl.pruebas === true;
  const setSoloPruebas = (v: boolean) => setFiltros({ pruebas: v || undefined });
  const [confirmarBorradoPruebas, setConfirmarBorradoPruebas] = useState(false);

  const { data: tecnicos } = useQuery({
    queryKey: ['employees', 'tecnicos'],
    queryFn: () => employeesApi.listEmployees({ rol: 'TECNICO', activo: true, take: 100 }),
  });

  /**
   * Filtros ACTIVOS. El listado y el export (S-06) comparten este objeto a
   * propósito: un export que ignorara los filtros sería una trampa — el dueño
   * se descargaría creyendo tener lo que estaba viendo en pantalla.
   */
  const filtros = useMemo(
    () => ({
      estado: estado || undefined,
      // A5: el numero del tablero excluye los remotos; el listado tambien.
      esRemoto: filtrosUrl.campo ? false : undefined,
      employeeId: employeeId || undefined,
      vencidos: soloVencidos || undefined,
      fueraDeRadio: soloFueraDeRadio || undefined,
      garantia: soloGarantias || undefined,
      esPrueba: soloPruebas || undefined,
      buscar: buscarDebounced || undefined,
      orden,
      from: rango.from,
      to: rango.to,
    }),
    [estado, employeeId, soloVencidos, soloFueraDeRadio, soloGarantias, soloPruebas, buscarDebounced, orden, filtrosUrl.campo, rango.from, rango.to],
  );

  // Conteo de registros de prueba: se pide siempre para poder ofrecer la
  // limpieza sin que el dueño tenga que ir a buscarla.
  const conteoPruebas = useQuery({
    queryKey: ['jobs', 'registros-de-prueba', 'conteo'],
    queryFn: jobsApi.contarRegistrosDePrueba,
  });

  const borrarPruebas = useMutation({
    mutationFn: jobsApi.borrarRegistrosDePrueba,
    onSuccess: ({ borrados }) => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success(
        borrados === 1 ? 'Se borró 1 registro de prueba' : `Se borraron ${borrados} registros de prueba`,
      );
      setConfirmarBorradoPruebas(false);
      setSoloPruebas(false);
    },
    onError: () => toast.error('No se pudieron borrar los registros de prueba'),
  });

  const marcarPrueba = useMutation({
    mutationFn: ({ id, valor }: { id: string; valor: boolean }) =>
      jobsApi.setEsPrueba(id, valor),
    onSuccess: ({ job, garantiasArrastradas }) => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      const base = job.esPrueba ? 'Marcado como prueba' : 'Ya no es un registro de prueba';
      toast.success(
        garantiasArrastradas > 0
          ? `${base} — también ${garantiasArrastradas} garantía(s) suya(s)`
          : base,
      );
    },
    onError: () => toast.error('No se pudo cambiar la marca de prueba'),
  });

  const exportar = useMutation({
    mutationFn: () => jobsApi.downloadJobsExport(filtros),
    onError: () => toast.error('No se pudo generar el Excel'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', 'list', filtros],
    queryFn: () => jobsApi.listJobs({ ...filtros, take: 100 }),
  });

  const assign = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      jobsApi.assignEmployee(id, value || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Técnico actualizado');
    },
    onError: () => toast.error('No se pudo asignar el técnico'),
  });

  const status = useMutation({
    mutationFn: ({ id, value }: { id: string; value: JobStatus }) =>
      jobsApi.changeStatus(id, { estado: value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Estado actualizado');
    },
    onError: () => toast.error('No se pudo cambiar el estado'),
  });

  const warranty = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) => jobsApi.setWarranty(id, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Garantía actualizada');
    },
    onError: () => toast.error('No se pudo actualizar la garantía'),
  });

  // Nuevo trabajo manual (Fase 1): venta hecha por fuera, sin pasar por el lead.
  const [nuevoOpen, setNuevoOpen] = useState(false);
  // A6 · La vista rápida. Estado LOCAL y no en la URL a propósito: si viviera en
  // la URL, el botón "atrás" cerraría el panel en vez de volver a la pantalla
  // anterior, que es lo que la gente espera de "atrás".
  const [vistaRapida, setVistaRapida] = useState<JobWithRelations | null>(null);

  // Edición completa (modal), historial de la ficha y borrado con confirmación.
  const [editar, setEditar] = useState<JobWithRelations | null>(null);
  const [historial, setHistorial] = useState<JobWithRelations | null>(null);
  // Fase 2: cancelación (motivo obligatorio) y reporte de cierre editable.
  const [cancelar, setCancelar] = useState<JobWithRelations | null>(null);
  const [editarCierre, setEditarCierre] = useState<JobWithRelations | null>(null);
  // S-01: trabajo que el técnico cerró y espera la validación de supervisión.
  const [validar, setValidar] = useState<JobWithRelations | null>(null);
  // S-03: instalación desde la que se abre una garantía (hereda sus datos).
  const [garantiaDe, setGarantiaDe] = useState<JobWithRelations | null>(null);
  // Garantía SUELTA: sin instalación en el sistema; el cliente se elige (o se
  // da de alta) adentro del propio modal.
  const [garantiaSuelta, setGarantiaSuelta] = useState(false);
  // S-12 · Enlace del trabajo con la conversación que lo originó.
  const [vincular, setVincular] = useState<JobWithRelations | null>(null);
  // S-04 · Los dos finales de un caso remoto.
  const [cerrarRemoto, setCerrarRemoto] = useState<JobWithRelations | null>(null);
  const [escalar, setEscalar] = useState<JobWithRelations | null>(null);
  const [borrar, setBorrar] = useState<JobWithRelations | null>(null);
  const del = useMutation({
    mutationFn: (job: JobWithRelations) => jobsApi.deleteJob(job.id, esBorradoProtegido(job)),
    onSuccess: () => {
      setBorrar(null);
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Trabajo borrado');
    },
    onError: () => toast.error('No se pudo borrar el trabajo'),
  });

  /**
   * Las acciones secundarias de una fila, ya filtradas por el estado del
   * trabajo. Antes eran ocho botones de solo ícono en fila: nadie recuerda qué
   * hace el frasco ni el clip, y se comían el ancho del cliente y la dirección.
   * Acá cada una lleva su NOMBRE escrito y viven dentro del menú «⋯».
   *
   * DOS quedan afuera del menú a propósito: «Editar trabajo» y «Abrir garantía
   * de esta instalación». Son las dos que el Asistente ILUMINA en la guía en
   * vivo, y no se puede iluminar algo que está adentro de un menú cerrado — el
   * usuario vería el foco puesto en la nada. Siguen como íconos en la fila,
   * que además son las dos acciones más usadas.
   */
  const accionesDeFila = (job: JobWithRelations): AccionDeMenu[] => {
    const acciones: AccionDeMenu[] = [];
    if (job.estado === 'ASIGNADO' || job.estado === 'EN_SITIO') {
      acciones.push({
        label: 'Cancelar trabajo',
        ayuda: 'El cliente no llegó u otra causa',
        icono: <Ban className="h-4 w-4" />,
        onClick: () => setCancelar(job),
      });
    }
    if (job.estado === 'COMPLETADO' || job.estado === 'NO_COMPLETADO') {
      acciones.push({
        label: 'Editar el reporte de cierre',
        ayuda: 'Corrige lo que reportó el técnico',
        icono: <ClipboardEdit className="h-4 w-4" />,
        onClick: () => setEditarCierre(job),
      });
    }
    // La marca de garantía se ve UNA vez (el chip, arriba) y se cambia UNA vez
    // (acá). Antes estaban las dos cosas en la fila: el chip y una casilla al
    // lado, diciendo lo mismo pegadas.
    acciones.push({
      label: job.esGarantia ? 'Quitar la marca de garantía' : 'Marcar como garantía',
      ayuda: 'Las garantías no generan comisión',
      icono: <ShieldCheck className="h-4 w-4" />,
      onClick: () => warranty.mutate({ id: job.id, value: !job.esGarantia }),
    });
    // S-12 · Enlace con la conversación del CRM.
    acciones.push({
      label: job.conversationId ? 'Cambiar la conversación enlazada' : 'Enlazar con una conversación',
      ayuda: job.conversationId ? undefined : 'Este trabajo no tiene chat asociado',
      icono: <Link2 className="h-4 w-4" />,
      onClick: () => setVincular(job),
    });
    acciones.push({
      label: job.esPrueba ? 'Quitar la marca de prueba' : 'Marcar como registro de prueba',
      ayuda: job.esPrueba ? 'Vuelve a los reportes' : 'Sale de los reportes',
      icono: <FlaskConical className="h-4 w-4" />,
      onClick: () => marcarPrueba.mutate({ id: job.id, valor: !job.esPrueba }),
    });
    acciones.push({
      label: 'Historial de la ficha',
      ayuda: 'Quién editó qué y cuándo',
      icono: <History className="h-4 w-4" />,
      onClick: () => setHistorial(job),
    });
    acciones.push({
      label: 'Borrar trabajo',
      icono: <Trash2 className="h-4 w-4" />,
      peligrosa: true,
      onClick: () => setBorrar(job),
    });
    return acciones;
  };

  const items = data?.items ?? [];
  const tecnicoFilterOptions = [
    { value: '', label: 'Todos los técnicos' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];
  const tecnicoAssignOptions = [
    { value: '', label: 'Sin asignar' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Trabajos"
        helpKey="jobs"
        description="Servicios agendados: asignación de técnico y estado."
        action={
          <div className="flex items-center gap-2">
            {/* S-06 · Se lleva a Excel EXACTAMENTE lo que hay en pantalla.
                Va por fetch + blob y NO por <a href>: la ruta exige el header
                Authorization, que una navegación del browser no manda (daría
                401). Mismo patrón que el export del CRM. */}
            <Button
              variant="outline"
              data-guia="jobs.exportar"
              onClick={() => exportar.mutate()}
              loading={exportar.isPending}
              title="Descarga los trabajos que se ven, con los filtros puestos"
            >
              <Download className="h-4 w-4" /> Exportar a Excel
            </Button>
            {/* Garantía SUELTA: el artículo no está en el sistema. Va acá, al
                lado de "Nuevo trabajo", porque es donde la oficina llegaba
                antes — y terminaba creando un trabajo normal con la casilla
                "Garantía" marcada, perdiendo plazo, prioridad y tipificación. */}
            <Button
              variant="outline"
              data-guia="jobs.abrir-garantia"
              onClick={() => setGarantiaSuelta(true)}
              title="Para un artículo que no está en el sistema"
            >
              <ShieldAlert className="h-4 w-4" /> Abrir garantía
            </Button>
            <Button data-guia="jobs.nuevo" onClick={() => setNuevoOpen(true)}>
              <Plus className="h-4 w-4" /> Nuevo trabajo
            </Button>
          </div>
        }
      />
      {/* A2 · Filtros guardados. Lee la ruta y el ?query de la URL por su cuenta. */}
      <FiltrosGuardados />
      <JobFormModal open={nuevoOpen} onClose={() => setNuevoOpen(false)} />
      <VistaRapidaTrabajo
        job={vistaRapida}
        onClose={() => setVistaRapida(null)}
        formatoFecha={(v) => citaLabel(typeof v === 'string' ? v : v?.toISOString()) ?? '—'}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          className="w-52"
          value={estado}
          options={estadoFilterOptions}
          onChange={(e) => setEstado(e.target.value as JobStatus | '')}
        />
        <Select
          className="w-52"
          value={employeeId}
          options={tecnicoFilterOptions}
          onChange={(e) => setEmployeeId(e.target.value)}
        />
        {/* Búsqueda por texto: antes, para encontrar un trabajo había que bajar
            leyendo nombre por nombre. */}
        <Input
          className="w-72"
          placeholder="Buscar cliente, dirección o descripción"
          aria-label="Buscar cliente, dirección o descripción"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
        />
        {/* El orden manda sobre la CITA, no sobre la fecha de carga: lo que el
            negocio mira es qué toca primero. */}
        <Select
          className="w-52"
          value={orden}
          options={ordenOptions}
          aria-label="Ordenar por"
          title="Ordenar por"
          onChange={(e) => setOrden(e.target.value as OrdenTrabajos)}
        />
        {/* Solo garantías: la lista del encargado de garantías. */}
        <button
          type="button"
          onClick={() => setSoloGarantias(!soloGarantias)}
          aria-pressed={soloGarantias}
          className={cn(
            'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
            soloGarantias
              ? 'border-amber-600 bg-amber-600 text-white'
              : 'border-surface-border bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300',
          )}
        >
          Solo garantías
        </button>
        {/* S-10 · Devuelve el mismo conjunto que Tracking de campo. */}
        <button
          type="button"
          onClick={() => setSoloVencidos(!soloVencidos)}
          aria-pressed={soloVencidos}
          className={cn(
            'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
            soloVencidos
              ? 'border-red-600 bg-red-600 text-white'
              : 'border-surface-border bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300',
          )}
        >
          Vencidos
        </button>
        {/* S-05 · Excepciones de ubicación: los cierres lejos del destino. */}
        <button
          type="button"
          onClick={() => setSoloFueraDeRadio(!soloFueraDeRadio)}
          aria-pressed={soloFueraDeRadio}
          className={cn(
            'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
            soloFueraDeRadio
              ? 'border-amber-600 bg-amber-600 text-white'
              : 'border-surface-border bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300',
          )}
        >
          Cerrados lejos
        </button>
        {/* Los registros de prueba están ocultos por defecto; este chip los
            saca a la luz para revisarlos y borrarlos al terminar. */}
        {(soloPruebas || (conteoPruebas.data?.total ?? 0) > 0) && (
          <button
            type="button"
            onClick={() => setSoloPruebas(!soloPruebas)}
            aria-pressed={soloPruebas}
            className={cn(
              'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
              soloPruebas
                ? 'border-amber-500 bg-amber-500 text-white'
                : 'border-dashed border-amber-400 bg-white text-amber-700 hover:bg-amber-50 dark:bg-slate-900',
            )}
          >
            Pruebas ({conteoPruebas.data?.total ?? 0})
          </button>
        )}
        <div className="flex items-center gap-1 rounded-lg border border-surface-border bg-white p-1 dark:border-slate-800 dark:bg-slate-900">
          {fechaChips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => setFecha(chip.value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                fecha === chip.value
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
        {fecha === 'personalizado' && (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
            />
            <span>→</span>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
        )}
        {fecha !== 'todo' && (
          <p className="text-xs text-slate-400">Se listan trabajos con cita en el rango.</p>
        )}
      </div>

      {soloPruebas && (conteoPruebas.data?.total ?? 0) > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-900">
            Está viendo <strong>{conteoPruebas.data?.total}</strong> registro(s) de prueba. No
            cuentan en los reportes ni en el monto vendido.
          </p>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setConfirmarBorradoPruebas(true)}
            loading={borrarPruebas.isPending}
          >
            <Trash2 className="mr-1 h-4 w-4" />
            Borrar todos los de prueba
          </Button>
        </div>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <Cargando />
          ) : items.length === 0 ? (
            <EmptyState
              icon={Wrench}
              title="Sin trabajos"
              description="Los trabajos se crean con el botón Agendar en la ficha del lead (dentro de la conversación)."
              className="m-5"
            />
          ) : (
            <ul data-guia="jobs.lista" className="divide-y divide-surface-border dark:divide-slate-800">
              {/* S-09 · Las garantías que reclaman atención YA van SEPARADAS y
                  arriba: mezcladas con el resto del día se pierden entre veinte
                  filas. Una garantía ya cerrada no entra aunque su plazo pasara. */}
              {(() => {
                const { vencidas, resto } = separarGarantiasVencidas(items);
                const grupoVencidas =
                  vencidas.length > 0
                    ? [{ key: 'garantias_vencidas' as const, label: 'Garantías que no pueden esperar', jobs: vencidas }]
                    : [];
                return [...grupoVencidas, ...groupJobs(resto)];
              })().map((group) => [
                <li
                  key={`header-${group.key}`}
                  className="bg-surface-muted px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400"
                >
                  {group.label} · {group.jobs.length}
                </li>,
                ...group.jobs.map((job) => {
                const badge = jobStatusBadge(job.estado);
                const cliente = job.contact?.name ?? job.contact?.pushName ?? job.contact?.phone ?? '—';
                const cita = citaLabel(job.scheduledAt);
                const espera = job.estado === 'ASIGNADO' ? esperaPendiente(job.scheduledAt) : null;
                return (
                  <li
                    key={job.id}
                    // A6 · Doble clic abre la vista rápida. Un clic simple NO
                    // hace nada: la fila tiene casillas y botones adentro, y
                    // abrir con un clic suelto haría que tocar un control abra
                    // el panel sin querer.
                    onDoubleClick={() => setVistaRapida(job)}
                    // COLUMNAS FIJAS: [cliente + descripción + cita + zona] ·
                    // [estado] · [técnico] · [acciones]. Antes era una sola fila
                    // que se envolvía sola, así que el estado y el técnico
                    // aparecían a distinta altura en cada línea y la lista no se
                    // podía leer en vertical. En pantalla angosta se apila
                    // (flex-col) en vez de mezclarse.
                    className="flex flex-col gap-3 px-5 py-3 hover:bg-slate-50 lg:flex-row lg:items-center dark:hover:bg-slate-800/40"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{cliente}</p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{job.descripcion}</p>
                      {/* Los chips viven CON el cliente y no sueltos al final de
                          la fila: son adjetivos de este trabajo, y al final se
                          leían como si fueran del de al lado. Solo aparecen
                          cuando hay algo que decir — un chip en cada fila deja
                          de significar nada. */}
                      <BadgesDeTrabajo job={job} />
                      {cita && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500 dark:text-slate-400">
                          <CalendarClock className="h-3 w-3 shrink-0" /> Cita: {cita}
                          {espera && (
                            <span
                              className={cn(
                                'ml-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
                                espera.vencida
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                              )}
                            >
                              {espera.label}
                            </span>
                          )}
                        </p>
                      )}
                      {(job.zona || job.direccion) && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-400">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {[job.zona, job.direccion].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {/* Timeline compacta (tanda ajustes-finales): mismo
                          componente del mapa/mis-trabajos, cero lógica nueva. */}
                      <JobTimeline estado={job.estado} className="mt-1.5 max-w-[260px]" />
                      {job.notas && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500 dark:text-slate-400">
                          <StickyNote className="h-3 w-3 shrink-0" /> {job.notas}
                        </p>
                      )}
                      {(job.registroArticulo || job.motivo) && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-600 dark:text-slate-300">
                          <ClipboardList className="h-3 w-3 shrink-0" />
                          {[
                            job.registroArticulo ? `Registro del técnico: “${job.registroArticulo}”` : null,
                            job.motivo ? `Motivo: ${job.motivo}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                      {/* No-show reportado por el técnico (Fase 2): visible mientras
                          el trabajo siga abierto — la decisión (cancelar/reagendar) es tuya. */}
                      {job.noShowReportedAt && (job.estado === 'ASIGNADO' || job.estado === 'EN_SITIO') && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs font-medium text-amber-700 dark:text-amber-400">
                          <UserX className="h-3 w-3 shrink-0" /> El técnico reportó que el cliente no llegó — cancelar o reagendar
                        </p>
                      )}
                      {(() => {
                        // Ubicación del cierre (Bloque 1): dónde ponchó Terminé/No terminé.
                        const cierre = closureZoneLabel(job);
                        if (!cierre) return null;
                        return (
                          <p
                            className={cn(
                              'mt-0.5 flex items-center gap-1 truncate text-xs',
                              cierre.enZona === true && 'text-emerald-600 dark:text-emerald-400',
                              cierre.enZona === false && 'text-danger',
                              cierre.enZona === null && 'text-slate-400',
                            )}
                          >
                            <MapPin className="h-3 w-3 shrink-0" /> {cierre.text}
                          </p>
                        );
                      })()}
                      {job.surveyResponse ? (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-600 dark:text-slate-300">
                          <MessageSquareQuote className="h-3 w-3 shrink-0" />
                          El cliente respondió: “{job.surveyResponse}”
                        </p>
                      ) : job.surveySentAt ? (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <Star className="h-3 w-3 shrink-0" /> Encuesta enviada (sin respuesta aún)
                        </p>
                      ) : null}
                    </div>
                    {/* ESTADO — una sola vez. Antes iba el desplegable Y un
                        chip al lado diciendo lo mismo: dos controles para un
                        dato, y el chip además no se podía tocar. Se quedó el
                        DESPLEGABLE porque dice el estado igual de claro y
                        además deja cambiarlo; el chip solo aparece cuando el
                        estado NO se puede cambiar desde acá (cancelado, o por
                        validar, que sale por su propio botón para no saltarse
                        el cobro). */}
                    <div className="w-44 shrink-0">
                      {job.estado !== 'CANCELADO' && job.estado !== 'PENDIENTE_VALIDACION' ? (
                        <Select
                          value={job.estado}
                          aria-label="Estado del trabajo"
                          options={estadoOptions}
                          disabled={status.isPending}
                          onChange={(e) => status.mutate({ id: job.id, value: e.target.value as JobStatus })}
                        />
                      ) : (
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      )}
                    </div>

                    {/* TÉCNICO */}
                    <div className="flex w-52 shrink-0 items-center gap-2">
                      {job.employee && <Avatar name={job.employee.nombre} src={job.employee.fotoUrl} size="sm" />}
                      <Select
                        className="w-40"
                        aria-label="Técnico asignado"
                        value={job.employeeId ?? ''}
                        options={tecnicoAssignOptions}
                        disabled={assign.isPending}
                        onChange={(e) => assign.mutate({ id: job.id, value: e.target.value })}
                      />
                    </div>

                    {/* ACCIONES: las principales con texto, el resto en «⋯». */}
                    <div className="flex shrink-0 items-center gap-1">
                      {/* S-04 · Un caso remoto no se ponchea: se cierra o se
                          escala desde acá. Son sus dos únicos finales. */}
                      {job.esRemoto && job.estado !== 'COMPLETADO' && job.estado !== 'CANCELADO' && (
                        <>
                          <Button size="sm" data-guia="jobs.fila.cerrar-caso" onClick={() => setCerrarRemoto(job)}>
                            <PhoneCall className="mr-1 h-4 w-4" />
                            Cerrar caso
                          </Button>
                          <Button size="sm" variant="outline" data-guia="jobs.fila.mandar-tecnico" onClick={() => setEscalar(job)}>
                            <ArrowUpRight className="mr-1 h-4 w-4" />
                            Mandar técnico
                          </Button>
                        </>
                      )}
                      {/* S-01: acción principal del estado "Por validar". Va como
                          botón con texto (no ícono): es LO que hay que hacer con
                          este trabajo, no una acción secundaria. */}
                      {job.estado === 'PENDIENTE_VALIDACION' && (
                        <Button size="sm" data-guia="jobs.fila.cerrar-trabajo" onClick={() => setValidar(job)}>
                          <ShieldCheck className="mr-1 h-4 w-4" />
                          Cerrar trabajo
                        </Button>
                      )}
                      {/* S-03: la garantía se abre DESDE la instalación, así
                          hereda cliente, modelo, serie, fecha e instalador sin
                          que nadie vuelva a digitarlos. Queda como ícono en la
                          fila (y no dentro del menú) porque la guía en vivo la
                          ilumina: no se puede iluminar algo que está adentro de
                          un menú cerrado. */}
                      {job.estado === 'COMPLETADO' && !job.esGarantia && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Abrir garantía de esta instalación"
                          title="Abrir garantía de esta instalación"
                          className="text-slate-400 hover:text-amber-600"
                          data-guia="jobs.fila.abrir-garantia"
                          onClick={() => setGarantiaDe(job)}
                        >
                          <ShieldAlert className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Misma razón que arriba: la guía ilumina este lápiz. */}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Editar trabajo"
                        title="Editar trabajo"
                        className="text-slate-400 hover:text-brand-600"
                        data-guia="jobs.fila.editar"
                        onClick={() => setEditar(job)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <MenuAcciones acciones={accionesDeFila(job)} />
                    </div>
                  </li>
                );
                }),
              ])}
            </ul>
          )}
        </CardBody>
      </Card>

      <EditarJobModal job={editar} onClose={() => setEditar(null)} />

      <JobHistoryModal job={historial} onClose={() => setHistorial(null)} />

      <CancelarJobModal job={cancelar} onClose={() => setCancelar(null)} />

      <EditarCierreModal job={editarCierre} onClose={() => setEditarCierre(null)} />

      <ValidarCierreModal job={validar} onClose={() => setValidar(null)} />

      <NuevaGarantiaModal
        open={Boolean(garantiaDe)}
        contactId={garantiaDe?.contactId ?? null}
        contactNombre={garantiaDe?.contact?.name ?? garantiaDe?.contact?.phone ?? null}
        instalacionInicialId={garantiaDe?.id ?? null}
        onClose={() => setGarantiaDe(null)}
      />

      <NuevaGarantiaModal
        open={garantiaSuelta}
        modo="suelta"
        contactId={null}
        onClose={() => setGarantiaSuelta(false)}
      />

      <VincularConversacionModal job={vincular} onClose={() => setVincular(null)} />

      <Modal
        open={confirmarBorradoPruebas}
        onClose={() => setConfirmarBorradoPruebas(false)}
        title="Borrar los registros de prueba"
        size="sm"
        description={`Se van a borrar ${conteoPruebas.data?.total ?? 0} trabajo(s) y garantía(s) marcados como prueba. Los registros reales no se tocan.`}
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmarBorradoPruebas(false)}>
              Volver
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => borrarPruebas.mutate()}
              loading={borrarPruebas.isPending}
            >
              Borrar todos
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          Esto no se puede deshacer. Si alguno de esos registros en realidad era real, quítele la
          marca de prueba antes de borrar.
        </p>
      </Modal>

      <CerrarCasoRemotoModal job={cerrarRemoto} onClose={() => setCerrarRemoto(null)} />

      <EscalarCasoModal job={escalar} onClose={() => setEscalar(null)} />

      <Modal
        open={Boolean(borrar)}
        onClose={() => setBorrar(null)}
        title="Borrar trabajo"
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setBorrar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={del.isPending}
              onClick={() => borrar && del.mutate(borrar)}
            >
              {borrar && esBorradoProtegido(borrar) ? 'Borrar de todos modos' : 'Borrar'}
            </Button>
          </>
        }
      >
        {borrar && (
          <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
            <p>
              Se va a borrar <span className="font-medium">“{borrar.descripcion}”</span>. Esta
              acción no se puede deshacer.
            </p>
            {esBorradoProtegido(borrar) && (
              <p className="rounded-md bg-amber-50 p-2 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Este trabajo está <b>completado y tiene la opinión del cliente</b>: “
                {borrar.surveyResponse}”. Borrarlo elimina también esa opinión de las
                estadísticas.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * Los chips de una fila: garantía, urgencia, prueba y las excepciones de cierre.
 *
 * Van todos juntos y pegados al cliente, no sueltos al final de la fila: son
 * adjetivos de ESTE trabajo, y al final se leían como si fueran del de al lado.
 * Solo se pintan cuando hay algo que decir — un chip en cada fila deja de
 * significar nada.
 */
function BadgesDeTrabajo({ job }: { job: JobWithRelations }) {
  const prioridad = prioridadGarantiaUI(job);
  const chips = [
    // "Garantía" aparece UNA sola vez. La casilla que estaba al lado diciendo
    // lo mismo se mudó al menú «⋯»: ver y cambiar son dos cosas distintas.
    job.esGarantia && (
      <Badge key="garantia" variant="warning" title="Las garantías no generan comisión">
        Garantía
      </Badge>
    ),
    // S-09 · La urgencia de una garantía se ve ANTES que nada.
    prioridad && (
      <Badge
        key="prioridad"
        variant={prioridad.nivel === 'maxima' ? 'danger' : 'warning'}
        title={prioridad.motivo}
      >
        {prioridad.etiqueta}
      </Badge>
    ),
    // Sin ficha no se adjudica: una garantía que el técnico cerró sin contar
    // qué hizo ni tipificarla tiene que verse distinta. Es lo que el dueño no
    // podía revisar: "dice que hizo diez garantías y yo no sé cuáles".
    fichaIncompletaUI(job) && (
      <Badge
        key="ficha"
        variant="danger"
        title="El técnico la cerró sin contar qué hizo o sin tipificarla: no cuenta como garantía atendida"
      >
        Ficha incompleta
      </Badge>
    ),
    // Registro de prueba: se ve distinto para no confundirlo con un trabajo
    // real de un vistazo.
    job.esPrueba && (
      <Badge
        key="prueba"
        variant="warning"
        title="Registro de prueba: no cuenta en los reportes ni en el monto vendido"
      >
        PRUEBA
      </Badge>
    ),
    // S-05 · Excepción de ubicación, bien visible: es lo que el dueño va a
    // querer revisar.
    job.cierreFueraDeRadio === true && (
      <Badge
        key="lejos"
        variant="warning"
        title={
          job.distanciaCierreM != null
            ? `Cerró a ${Math.round(job.distanciaCierreM)} m del destino`
            : 'El cierre quedó fuera de la distancia aceptable'
        }
      >
        Cerró lejos
        {job.distanciaCierreM != null ? ` · ${formatDistanceM(job.distanciaCierreM)}` : ''}
      </Badge>
    ),
    job.cierreSinGps === true && (
      <Badge key="sin-gps" variant="default" title="El teléfono no dio la ubicación al cerrar">
        Sin ubicación
      </Badge>
    ),
  ].filter(Boolean);

  if (chips.length === 0) return null;
  return <div className="mt-1 flex flex-wrap items-center gap-1.5">{chips}</div>;
}
